import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateAdditionalInformation } from "../../api/agency-profile";

const validationSchema = yup.object({
    mailingAddressLine1: yup
        .string()
        .required("Please enter the first line of the mailing address."),
    mailingAddressLine2: yup.string().optional(),
    mailingAddressCity: yup.string().required("Please enter the mailing city."),
    mailingAddressState: yup
        .string()
        .required("Please enter the mailing state.")
        .max(2, "State must be two letters.")
        .matches(/[a-zA-Z]+/g, {
            message: "State must contain only letters.",
            excludeEmptyString: true,
        }),
    mailingAddressZip: yup
        .string()
        .required("Please enter the mailing ZIP code."),
});

export default function MailingAddressForm({
    agencyProfile,
    setAgencyProfile,
    onSave,
    onCancel,
}) {

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const submitSuccessCallback = () => {
        console.log("Saved mailing address.");
    };
    const submitErrorCallback = () => {
        console.log("Error while saving mailing address.");
    };

    const onSubmit = (values) => {
        const updatedAgencyProfile = {
            ...agencyProfile,
            mailingAddress: {
                ...agencyProfile.mailingAddress,
                street1: values.mailingAddressLine1,
                street2: values.mailingAddressLine2,
                city: values.mailingAddressCity,
                state: values.mailingAddressState,
                zipCode: values.mailingAddressZip,
            },
        };

        setAgencyProfile(updatedAgencyProfile);

        updateAdditionalInformation(
            updatedAgencyProfile,
            submitSuccessCallback,
            submitErrorCallback
        );
        onSave(updatedAgencyProfile);
    };

    return (
        <form
            className="form"
            onSubmit={handleSubmit(onSubmit)}
            style={{ textAlign: "left" }}
        >
            <div
                className={`form-field large-text-input ${errors.mailingAddressLine1 ? "form-field--error" : ""
                    }`}
            >
                <label htmlFor="mailing-address-line-1" className="text">
                    Street 1
                </label>
                <input
                    id="mailing-address-line-1"
                    type="text"
                    size="30"
                    {...register("mailingAddressLine1", {
                        value: agencyProfile.mailingAddress?.street1 ?? "",
                    })}
                />
                {errors.mailingAddressLine1 && (
                    <span className="form-message">
                        {errors.mailingAddressLine1.message}
                    </span>
                )}
            </div>

            <div
                className={`form-field large-text-input ${errors.mailingAddressLine2 ? "form-field--error" : ""
                    }`}
            >
                <label htmlFor="mailing-address-line-2" className="text">
                    Street 2
                </label>
                <input
                    id="mailing-address-line-2"
                    type="text"
                    size="30"
                    {...register("mailingAddressLine2", {
                        value: agencyProfile.mailingAddress?.street2 ?? "",
                    })}
                />
                {errors.mailingAddressLine2 && (
                    <span className="form-message">
                        {errors.mailingAddressLine2.message}
                    </span>
                )}
            </div>

            <div className="row" style={{ textAlign: "left" }}>
                <div className="col-sm-6" style={{ marginRight: "10px" }}>
                    <div
                        className={`form-field large-text-input ${errors.mailingAddressCity ? "form-field--error" : ""
                            }`}
                    >
                        <label htmlFor="mailing-address-city" className="text">
                            City
                        </label>
                        <input
                            id="mailing-address-city"
                            type="text"
                            size="30"
                            {...register("mailingAddressCity", {
                                value: agencyProfile.mailingAddress?.city ?? "",
                            })}
                        />
                        {errors.mailingAddressCity && (
                            <span className="form-message">
                                {errors.mailingAddressCity.message}
                            </span>
                        )}
                    </div>
                </div>
                <div className="col-sm-6">
                    <div
                        className={`form-field large-text-input ${errors.mailingAddressState ? "form-field--error" : ""
                            }`}
                    >
                        <label htmlFor="mailing-address-state" className="text">
                            State
                        </label>
                        <input
                            id="mailing-address-state"
                            type="text"
                            size="5"
                            {...register("mailingAddressState", {
                                value: agencyProfile.mailingAddress?.state ?? "",
                            })}
                        />
                        {errors.mailingAddressState && (
                            <span className="form-message">
                                {errors.mailingAddressState.message}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div
                className={`form-field ${errors.mailingAddressZip ? "form-field--error" : ""
                    }`}
            >
                <label htmlFor="mailing-address-zip" className="text">
                    Zip
                </label>
                <input
                    id="mailing-address-zip"
                    type="text"
                    size="15"
                    {...register("mailingAddressZip", {
                        value: agencyProfile.mailingAddress?.zipCode ?? "",
                    })}
                />
                {errors.mailingAddressZip && (
                    <span className="form-message">
                        {errors.mailingAddressZip.message}
                    </span>
                )}
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <button
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                    >
                        <span>Save</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                        onClick={onCancel}
                    >
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
        </form>
    );
}
